import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Flame, Loader2, RefreshCw, Share2, Check } from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
import { SectionHeader } from "../components/UI";
import PushAlertSignup from "../components/PushAlertSignup";
import SaveSearchButton from "../components/SaveSearchButton";
import { getVehicles } from "../lib/api";
import { fmtMoney, fmtMiles } from "../config";

const POLL_MS = 60_000;          // refresh inventory once a minute
const RESULT_LIMIT = 10;

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  return `${Math.round(d / 30)} mo ago`;
}

export default function JustListed() {
  const [params] = useSearchParams();
  const pinnedSlug = params.get("slug");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [copied, setCopied] = useState(false);
  const prevSlugsRef = useRef(new Set());

  async function fetchVehicles(initial = false) {
    if (initial) setLoading(true); else setRefreshing(true);
    setErr("");
    try {
      const data = await getVehicles({ sort: "newest", limit: RESULT_LIMIT });
      const list = data?.items || [];
      // Tag NEW items (any slug that wasn't in the previous fetch)
      const prev = prevSlugsRef.current;
      const tagged = list.map((v) => ({ ...v, _justListed: !initial && prev.size > 0 && !prev.has(v.slug) }));
      prevSlugsRef.current = new Set(list.map((v) => v.slug));
      setItems(tagged);
      setLastUpdated(new Date());
    } catch {
      setErr("Couldn't refresh listings. Trying again shortly.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  // Initial + interval polling
  useEffect(() => {
    fetchVehicles(true);
    const id = setInterval(() => fetchVehicles(false), POLL_MS);
    // Also refresh when tab regains focus
    const onVis = () => { if (document.visibilityState === "visible") fetchVehicles(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort: pinned slug first, then the rest in feed order
  const ordered = useMemo(() => {
    if (!pinnedSlug) return items;
    const pinned = items.find((v) => v.slug === pinnedSlug);
    if (!pinned) return items;
    return [pinned, ...items.filter((v) => v.slug !== pinnedSlug)];
  }, [items, pinnedSlug]);

  async function shareList() {
    const url = window.location.origin + "/just-listed";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Just Listed — Scott Sells Chevys", text: "Newest vehicles on Scott's lot", url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* user dismissed */ }
  }

  return (
    <main id="main-content" data-testid="just-listed-page">
      <Seo
        title="Just Listed — New & Used Vehicles This Week | Scott Sells Chevys"
        description="See the 10 newest vehicles on Scott Hall's lot. Refreshed live. Sign up for push alerts the moment a match arrives."
        canonicalPath="/just-listed"
        jsonLd={[breadcrumbs([{ name: "Home", path: "/" }, { name: "Just Listed", path: "/just-listed" }])]}
      />

      {/* Hero */}
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--ssc-blue)]/15 border border-[var(--ssc-blue)]/40 text-[var(--ssc-blue)] text-xs font-black uppercase tracking-wide mb-3">
            <Flame size={14} /> Hot this week
          </div>
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Just Listed.</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">
            The 10 newest vehicles on Scott's lot — refreshed live. See something you like? Tap it for the full listing or set up an instant push alert below for the next match.
          </p>
          <div className="mt-4 flex items-center gap-3 text-xs text-[var(--ssc-silver)]" data-testid="just-listed-status">
            {refreshing ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            <span>
              {lastUpdated ? `Updated ${relTime(lastUpdated.toISOString())}` : "Loading…"}
            </span>
            <button
              type="button"
              onClick={() => fetchVehicles(false)}
              className="ml-2 text-[var(--ssc-blue)] font-bold hover:underline"
              data-testid="just-listed-refresh"
            >
              Refresh now
            </button>
            <button
              type="button"
              onClick={shareList}
              className="ml-2 inline-flex items-center gap-1 text-[var(--ssc-blue)] font-bold hover:underline"
              data-testid="just-listed-share"
            >
              {copied ? <><Check size={14} /> Copied</> : <><Share2 size={14} /> Share</>}
            </button>
            <span className="ml-2">
              <SaveSearchButton
                label="Just Listed feed"
                criteria={{ new_or_used: "either" }}
              />
            </span>
          </div>
        </div>
      </section>

      {/* Push signup */}
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <SectionHeader
            title="Want a heads-up the moment a match arrives?"
            subtitle="Skip the inbox. Get a push notification — works after you install the app."
          />
          <PushAlertSignup />
        </div>
      </section>

      {/* Listings */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <SectionHeader title="Newest on the lot" />

          {loading ? (
            <div className="flex items-center gap-2 text-[var(--ssc-ink-2)]" data-testid="just-listed-loading">
              <Loader2 className="animate-spin" size={18} /> Loading freshest inventory…
            </div>
          ) : err ? (
            <div className="rounded-xl bg-[#FFF4F4] border border-[#FFD7D7] text-[var(--ssc-red)] px-4 py-3 text-sm font-bold">
              {err}
            </div>
          ) : ordered.length === 0 ? (
            <div className="ssc-card text-[var(--ssc-ink-2)]">
              No vehicles on the lot right now. Sign up above and you'll be the first to know when one lands.
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="just-listed-grid">
              {ordered.map((v) => (
                <ListingCard key={v.slug} v={v} pinned={v.slug === pinnedSlug} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}

function ListingCard({ v, pinned }) {
  const photo = v.photos?.[0]?.url;
  const alt = v.photos?.[0]?.alt || `${v.year} ${v.make} ${v.model}`;
  const subtitle = [
    v.trim,
    v.mileage ? fmtMiles(v.mileage) : null,
    v.exterior_color,
  ].filter(Boolean).join(" · ");

  const shareUrl = `${window.location.origin}/just-listed?slug=${encodeURIComponent(v.slug)}`;

  async function copyDeepLink(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({ title: `${v.year} ${v.make} ${v.model}`, text: subtitle, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch { /* user dismissed */ }
  }

  return (
    <li
      className={`relative rounded-2xl bg-white border ${pinned ? "border-[var(--ssc-blue)] ring-2 ring-[var(--ssc-blue)]/30" : "border-[#D9E1EA]"} overflow-hidden flex flex-col`}
      data-testid={`just-listed-card-${v.slug}`}
    >
      {pinned && (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-[var(--ssc-blue)] text-white text-xs font-black uppercase tracking-wide shadow-md">
          Shared with you
        </div>
      )}
      {v._justListed && !pinned && (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-[#10a37f] text-white text-xs font-black uppercase tracking-wide shadow-md flex items-center gap-1">
          <Flame size={12} /> Just added
        </div>
      )}
      {photo ? (
        <Link to={`/${v.status}-vehicles/${v.slug}`} className="block">
          <img src={photo} alt={alt} className="w-full h-44 object-cover" loading="lazy" />
        </Link>
      ) : (
        <div className="w-full h-44 bg-[#F4F7FB] flex items-center justify-center text-[#9aa6b8] font-bold">
          Photo coming
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <Link to={`/${v.status}-vehicles/${v.slug}`} className="font-extrabold text-[var(--ssc-navy)] leading-tight hover:text-[var(--ssc-blue)]">
          {v.year} {v.make} {v.model}
        </Link>
        {subtitle && <div className="text-sm text-[#6b7c93]">{subtitle}</div>}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="text-xl font-black text-[var(--ssc-navy)] tabular-nums">{fmtMoney(v.price)}</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyDeepLink}
              className="text-[#6b7c93] hover:text-[var(--ssc-blue)] p-1"
              title="Share this listing"
              aria-label="Share this listing"
              data-testid="just-listed-card-share"
            >
              <Share2 size={16} />
            </button>
            <Link
              to={`/${v.status}-vehicles/${v.slug}`}
              className="ssc-btn ssc-btn-primary text-xs px-3 py-2"
              data-testid="just-listed-card-view"
            >
              View
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}
